import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { settingsAPI } from "../services/api";
import {
  Percent, MapPin, Tag, Save, Plus, Trash2,
  RefreshCw, ChevronDown, ChevronUp, Shield, Lock
} from "lucide-react";
import toast from "react-hot-toast";

const TABS = [
  { id: "commission",  label: "Commission",    icon: Percent, roles: ["super_admin","admin","branch_manager"] },
  { id: "zones",       label: "Zones",         icon: MapPin,  roles: ["super_admin"] },
  { id: "promotions",  label: "Promos",        icon: Tag,     roles: ["super_admin","admin"] },
];

const blankZone  = { name:"", city:"", base_fee:"5.00", price_per_km:"1.50", surge_multiplier:"1.00" };
const blankPromo = { code:"", type:"percentage", value:"", min_order:"0", max_uses:"", expires_at:"" };

const LockedSection = ({ message }) => (
  <div style={{
    display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
    gap:12, padding:"60px 20px", color:"var(--text3)"
  }}>
    <Lock size={32} style={{opacity:0.4}}/>
    <p style={{fontSize:14}}>{message}</p>
  </div>
);

// ─── COMMISSION TAB ───────────────────────────────────────────────────────────
function CommissionTab({ user }) {
  const isSuperAdmin = user.role === "super_admin";
  const isAdmin      = ["admin","super_admin"].includes(user.role);
  const isReadOnly   = user.role === "branch_manager";

  const [rates, setRates]             = useState([]);
  const [defaultRate, setDefaultRate] = useState(null);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState({});
  const [savingDefault, setSavingDefault] = useState(false);
  const [draftDefault, setDraftDefault]   = useState("");
  const [drafts, setDrafts]           = useState({});
  const [expanded, setExpanded]       = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await settingsAPI.getCommissionRates();
      const data = r.data.data;
      setRates(data.riders || []);
      const def = data.default_rate ?? 0.20;
      setDefaultRate(def);
      setDraftDefault((def * 100).toFixed(0));
    } catch { toast.error("Failed to load commission rates"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const initDraft = (rider) => {
    if (!drafts[rider.rider_id]) {
      setDrafts(d => ({ ...d, [rider.rider_id]: {
        rate: ((rider.rate ?? defaultRate) * 100).toFixed(0),
        note: rider.note || ""
      }}));
    }
  };

  const toggleExpand = (riderId) => setExpanded(e => ({ ...e, [riderId]: !e[riderId] }));

  const saveRiderRate = async (riderId) => {
    const draft = drafts[riderId];
    if (!draft) return;
    const rateDecimal = parseFloat(draft.rate) / 100;
    if (isNaN(rateDecimal) || rateDecimal < 0 || rateDecimal > 1) { toast.error("Rate must be between 0% and 100%"); return; }
    setSaving(s => ({ ...s, [riderId]: true }));
    try {
      await settingsAPI.setRiderRate(riderId, { rate: rateDecimal, note: draft.note });
      toast.success("Rate updated"); load();
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
    finally { setSaving(s => ({ ...s, [riderId]: false })); }
  };

  const saveDefault = async () => {
    const rateDecimal = parseFloat(draftDefault) / 100;
    if (isNaN(rateDecimal) || rateDecimal < 0 || rateDecimal > 1) { toast.error("Rate must be between 0% and 100%"); return; }
    setSavingDefault(true);
    try {
      await settingsAPI.setDefaultRate({ rate: rateDecimal });
      toast.success("Default rate updated"); load();
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
    finally { setSavingDefault(false); }
  };

  if (loading) return <div className="loading-center"><div className="spinner"/><span>Loading...</span></div>;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      {/* Default rate card */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">🌐 Global Default Rate</span>
          {!isSuperAdmin && (
            <span style={{fontSize:11,color:"var(--text3)",display:"flex",alignItems:"center",gap:4}}>
              <Lock size={11}/> Super Admin only
            </span>
          )}
        </div>
        <div className="card-body" style={{padding:"16px 20px"}}>
          <p style={{fontSize:13,color:"var(--text2)",marginBottom:16}}>
            Applied to all riders without a custom rate. Currently <strong style={{color:"var(--accent)"}}>{defaultRate !== null ? (defaultRate*100).toFixed(0) : "—"}%</strong> rider commission.
          </p>
          <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <div style={{position:"relative",width:120}}>
              <input
                className="input" type="number" min="0" max="100"
                value={draftDefault}
                onChange={e => setDraftDefault(e.target.value)}
                disabled={!isSuperAdmin}
                style={{paddingRight:28}}
              />
              <span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",color:"var(--text3)",fontSize:13}}>%</span>
            </div>
            {isSuperAdmin && (
              <button className="btn btn-primary btn-sm" onClick={saveDefault} disabled={savingDefault}>
                <Save size={13}/> {savingDefault ? "Saving..." : "Save Default"}
              </button>
            )}
            {!isSuperAdmin && (
              <span style={{fontSize:12,color:"var(--text3)"}}>Only Super Admins can change the global default.</span>
            )}
          </div>
        </div>
      </div>

      {/* Per-rider rates */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">👤 Per-Rider Rates</span>
          <span style={{fontSize:12,color:"var(--text3)"}}>{rates.length} riders</span>
        </div>
        {isReadOnly && (
          <div style={{padding:"10px 20px 0",fontSize:12,color:"var(--text3)",display:"flex",alignItems:"center",gap:6}}>
            <Lock size={11}/> View only — contact an admin to change rates.
          </div>
        )}

        {/* Mobile rider rate cards */}
        <div style={{display:"block"}} className="comm-mobile">
          {rates.length === 0
            ? <div className="empty-state"><p>No riders found</p></div>
            : rates.map(rider => {
              const isExpanded  = expanded[rider.rider_id];
              const draft       = drafts[rider.rider_id];
              const effectiveRate = rider.rate ?? defaultRate ?? 0.20;
              const hasCustom   = rider.rate !== null && rider.rate !== undefined;
              return (
                <div key={rider.rider_id} style={{borderBottom:"1px solid var(--border)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:14}}>{rider.name}</div>
                      <div style={{fontSize:11,color:"var(--text3)"}}>{rider.phone}</div>
                      {rider.note && <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{rider.note}</div>}
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <span style={{fontWeight:700,fontSize:16,color:hasCustom?"var(--accent)":"var(--text2)"}}>
                        {(effectiveRate * 100).toFixed(0)}%
                      </span>
                      {!hasCustom && <div style={{fontSize:10,color:"var(--text3)"}}>default</div>}
                      {hasCustom  && <div style={{fontSize:10,color:"var(--accent)"}}>custom</div>}
                    </div>
                    {isAdmin && (
                      <button className="btn btn-ghost btn-sm" onClick={() => { initDraft(rider); toggleExpand(rider.rider_id); }}>
                        {isExpanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                      </button>
                    )}
                  </div>
                  {isExpanded && isAdmin && draft && (
                    <div style={{padding:"12px 16px",background:"var(--bg3)",display:"flex",flexDirection:"column",gap:10}}>
                      <div className="form-group" style={{marginBottom:0}}>
                        <label className="form-label">Commission Rate (%)</label>
                        <div style={{position:"relative",width:120}}>
                          <input
                            className="input input-sm" type="number" min="0" max="100"
                            value={draft.rate}
                            onChange={e => setDrafts(d => ({ ...d, [rider.rider_id]: {...d[rider.rider_id], rate: e.target.value} }))}
                            style={{paddingRight:24}}
                          />
                          <span style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",color:"var(--text3)",fontSize:12}}>%</span>
                        </div>
                      </div>
                      <div className="form-group" style={{marginBottom:0}}>
                        <label className="form-label">Note (optional)</label>
                        <input
                          className="input input-sm" placeholder="e.g. Senior rider bonus"
                          value={draft.note}
                          onChange={e => setDrafts(d => ({ ...d, [rider.rider_id]: {...d[rider.rider_id], note: e.target.value} }))}
                        />
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <button className="btn btn-primary btn-sm" onClick={() => saveRiderRate(rider.rider_id)} disabled={saving[rider.rider_id]}>
                          <Save size={13}/> {saving[rider.rider_id] ? "Saving..." : "Save"}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => toggleExpand(rider.rider_id)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          }
        </div>
      </div>
    </div>
  );
}

// ─── DELIVERY ZONES TAB ───────────────────────────────────────────────────────
function ZonesTab({ user }) {
  const isSuperAdmin = user.role === "super_admin";

  const [zones, setZones]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState(blankZone);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await settingsAPI.getZones();
      setZones(r.data.data || []);
    } catch { toast.error("Failed to load zones"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(blankZone); setModal(true); };
  const openEdit   = (z)  => {
    setEditing(z.id);
    setForm({ name: z.name, city: z.city, base_fee: z.base_fee, price_per_km: z.price_per_km, surge_multiplier: z.surge_multiplier });
    setModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.city) { toast.error("Name and city are required"); return; }
    setSaving(true);
    try {
      if (editing) { await settingsAPI.updateZone(editing, form); toast.success("Zone updated"); }
      else         { await settingsAPI.createZone(form); toast.success("Zone created"); }
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const handleToggle = async (z) => {
    try {
      await settingsAPI.updateZone(z.id, { ...z, is_active: z.is_active ? 0 : 1 });
      toast.success(z.is_active ? "Zone deactivated" : "Zone activated"); load();
    } catch { toast.error("Failed"); }
  };

  const handleDelete = async (z) => {
    if (!window.confirm(`Deactivate zone "${z.name}"?`)) return;
    try { await settingsAPI.deleteZone(z.id); toast.success("Zone deactivated"); load(); }
    catch { toast.error("Failed"); }
  };

  if (!isSuperAdmin) return <LockedSection message="Only Super Admins can manage delivery zones."/>;
  if (loading) return <div className="loading-center"><div className="spinner"/><span>Loading...</span></div>;

  return (
    <div>
      <style>{`
        .zones-mobile-cards  { display: none; }
        .zones-desktop-table { display: block; }
        .zone-card { padding: 14px 16px; border-bottom: 1px solid var(--border); }
        .zone-card:last-child { border-bottom: none; }
        @media (max-width: 768px) {
          .zones-mobile-cards  { display: block; }
          .zones-desktop-table { display: none; }
        }
      `}</style>

      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={14}/> New Zone</button>
      </div>

      <div className="card">
        {/* Mobile */}
        <div className="zones-mobile-cards">
          {zones.length === 0
            ? <div className="empty-state"><p>No zones yet</p></div>
            : zones.map(z => (
            <div key={z.id} className="zone-card">
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14}}>{z.name}</div>
                  <div style={{fontSize:12,color:"var(--text3)"}}>{z.city}</div>
                </div>
                <span className={`badge ${z.is_active?"badge-active":"badge-suspended"}`}>{z.is_active?"Active":"Inactive"}</span>
              </div>
              <div style={{display:"flex",gap:16,fontSize:12,color:"var(--text2)",marginBottom:8,flexWrap:"wrap"}}>
                <span>Base: GHS {parseFloat(z.base_fee).toFixed(2)}</span>
                <span>Per KM: GHS {parseFloat(z.price_per_km).toFixed(2)}</span>
                <span style={{color:parseFloat(z.surge_multiplier)>1?"var(--accent)":"var(--text2)"}}>Surge: {parseFloat(z.surge_multiplier).toFixed(2)}×</span>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button className="btn btn-ghost btn-sm" onClick={() => openEdit(z)}>Edit</button>
                <button className={`btn btn-sm ${z.is_active?"btn-ghost":"btn-success"}`} onClick={() => handleToggle(z)}>
                  {z.is_active?"Pause":"Activate"}
                </button>
                <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDelete(z)}><Trash2 size={13}/></button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop */}
        <div className="zones-desktop-table table-wrap">
          <table>
            <thead><tr><th>Zone Name</th><th>City</th><th>Base Fee</th><th>Per KM</th><th>Surge</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {zones.length === 0
                ? <tr><td colSpan={7}><div className="empty-state"><p>No zones yet</p></div></td></tr>
                : zones.map(z => (
                <tr key={z.id}>
                  <td className="fw-bold">{z.name}</td>
                  <td style={{color:"var(--text2)"}}>{z.city}</td>
                  <td className="mono">GHS {parseFloat(z.base_fee).toFixed(2)}</td>
                  <td className="mono">GHS {parseFloat(z.price_per_km).toFixed(2)}</td>
                  <td className="mono">
                    <span style={{color:parseFloat(z.surge_multiplier)>1?"var(--accent)":"var(--text2)"}}>
                      {parseFloat(z.surge_multiplier).toFixed(2)}×
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${z.is_active?"badge-active":"badge-suspended"}`}>
                      {z.is_active?"Active":"Inactive"}
                    </span>
                  </td>
                  <td>
                    <div style={{display:"flex",gap:4}}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(z)}>Edit</button>
                      <button className={`btn btn-sm ${z.is_active?"btn-ghost":"btn-success"}`} onClick={() => handleToggle(z)}>
                        {z.is_active?"Pause":"Activate"}
                      </button>
                      <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDelete(z)}><Trash2 size={13}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{editing ? "Edit Zone" : "New Delivery Zone"}</div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Zone Name</label>
                  <input className="input" placeholder="East Legon" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input className="input" placeholder="Accra" value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))}/>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Base Fee (GHS)</label>
                  <input className="input" type="number" step="0.50" value={form.base_fee} onChange={e => setForm(f => ({...f, base_fee: e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Price Per KM (GHS)</label>
                  <input className="input" type="number" step="0.10" value={form.price_per_km} onChange={e => setForm(f => ({...f, price_per_km: e.target.value}))}/>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Surge Multiplier <span style={{color:"var(--text3)",fontWeight:400}}>(1.00 = no surge)</span></label>
                <input className="input" type="number" step="0.10" min="1" value={form.surge_multiplier} onChange={e => setForm(f => ({...f, surge_multiplier: e.target.value}))}/>
              </div>
              <div style={{background:"rgba(245,166,35,0.07)",border:"1px solid rgba(245,166,35,0.2)",borderRadius:8,padding:"12px 16px",marginTop:4}}>
                <div style={{fontSize:11,color:"var(--text3)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.5px"}}>Price Preview</div>
                <div style={{fontSize:13,color:"var(--text2)"}}>
                  5 km: <strong style={{color:"var(--accent)"}}>GHS {((parseFloat(form.base_fee)||0)+(parseFloat(form.price_per_km)||0)*5*(parseFloat(form.surge_multiplier)||1)).toFixed(2)}</strong>
                  {" "}· 10 km: <strong style={{color:"var(--accent)"}}>GHS {((parseFloat(form.base_fee)||0)+(parseFloat(form.price_per_km)||0)*10*(parseFloat(form.surge_multiplier)||1)).toFixed(2)}</strong>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                <Save size={13}/> {saving ? "Saving..." : editing ? "Update Zone" : "Create Zone"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PROMOTIONS TAB ───────────────────────────────────────────────────────────
function PromotionsTab({ user }) {
  const isAdmin = ["admin","super_admin"].includes(user.role);

  const [promos, setPromos]   = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState(blankPromo);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await settingsAPI.getPromos({ page, limit: 15 });
      setPromos(r.data.data || []); setTotal(r.data.pagination?.total || 0);
    } catch { toast.error("Failed to load promotions"); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await settingsAPI.createPromo(form);
      toast.success("Promotion created!");
      setModal(false); setForm(blankPromo); load();
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const handleToggle = async (p) => {
    try {
      await settingsAPI.updatePromo(p.id, { ...p, is_active: p.is_active ? 0 : 1 });
      toast.success(p.is_active ? "Deactivated" : "Activated"); load();
    } catch { toast.error("Failed"); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete promo "${p.code}"?`)) return;
    try { await settingsAPI.deletePromo(p.id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const pages = Math.ceil(total / 15);

  if (!isAdmin) return <LockedSection message="Only Admins can manage promotions."/>;
  if (loading) return <div className="loading-center"><div className="spinner"/><span>Loading...</span></div>;

  return (
    <div>
      <style>{`
        .sp-mobile-cards  { display: none; }
        .sp-desktop-table { display: block; }
        .sp-card { padding: 14px 16px; border-bottom: 1px solid var(--border); }
        .sp-card:last-child { border-bottom: none; }
        @media (max-width: 768px) {
          .sp-mobile-cards  { display: block; }
          .sp-desktop-table { display: none; }
        }
      `}</style>

      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
        <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={14}/> New Promo</button>
      </div>

      <div className="card">
        {/* Mobile */}
        <div className="sp-mobile-cards">
          {promos.length === 0
            ? <div className="empty-state"><p>No promotions yet</p></div>
            : promos.map(p => (
            <div key={p.id} className="sp-card">
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div>
                  <span className="mono" style={{color:"var(--accent)",fontWeight:700,fontSize:14}}>{p.code}</span>
                  <span className={`badge ${p.is_active?"badge-active":"badge-suspended"}`} style={{marginLeft:8}}>{p.is_active?"Active":"Inactive"}</span>
                </div>
                <span className="fw-bold">{p.type==="percentage"?`${p.value}%`:p.type==="fixed"?`GHS ${p.value}`:"Free"}</span>
              </div>
              <div style={{fontSize:12,color:"var(--text3)",marginBottom:8,display:"flex",gap:12,flexWrap:"wrap"}}>
                <span style={{textTransform:"capitalize"}}>{p.type.replace(/_/g," ")}</span>
                <span>Min: GHS {p.min_order}</span>
                <span>Used {p.used_count}/{p.max_uses||"∞"}</span>
                <span>{p.expires_at ? new Date(p.expires_at).toLocaleDateString("en-GH",{day:"numeric",month:"short"}) : "No expiry"}</span>
              </div>
              <div style={{display:"flex",gap:6}}>
                <button className={`btn btn-sm ${p.is_active?"btn-ghost":"btn-success"}`} onClick={() => handleToggle(p)}>{p.is_active?"Pause":"Activate"}</button>
                <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDelete(p)}><Trash2 size={13}/></button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop */}
        <div className="sp-desktop-table table-wrap">
          <table>
            <thead><tr><th>Code</th><th>Type</th><th>Value</th><th>Min Order</th><th>Used</th><th>Max Uses</th><th>Expires</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {promos.length === 0
                ? <tr><td colSpan={9}><div className="empty-state"><p>No promotions yet</p></div></td></tr>
                : promos.map(p => (
                <tr key={p.id}>
                  <td><span className="mono" style={{color:"var(--accent)",fontWeight:700,fontSize:13}}>{p.code}</span></td>
                  <td style={{textTransform:"capitalize",color:"var(--text2)"}}>{p.type.replace(/_/g," ")}</td>
                  <td className="fw-bold">{p.type==="percentage"?`${p.value}%`:p.type==="fixed"?`GHS ${p.value}`:"Free"}</td>
                  <td className="mono">GHS {p.min_order}</td>
                  <td className="mono">{p.used_count}</td>
                  <td className="mono">{p.max_uses || "∞"}</td>
                  <td className="mono" style={{fontSize:11,color:"var(--text3)"}}>
                    {p.expires_at ? new Date(p.expires_at).toLocaleDateString("en-GH",{day:"numeric",month:"short",year:"numeric"}) : "Never"}
                  </td>
                  <td><span className={`badge ${p.is_active?"badge-active":"badge-suspended"}`}>{p.is_active?"Active":"Inactive"}</span></td>
                  <td>
                    <div style={{display:"flex",gap:4}}>
                      <button className={`btn btn-sm ${p.is_active?"btn-ghost":"btn-success"}`} onClick={() => handleToggle(p)}>{p.is_active?"Pause":"Activate"}</button>
                      <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDelete(p)}><Trash2 size={13}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <span>Showing {promos.length} of {total}</span>
          <div className="pagination-btns">
            <button className="btn btn-ghost btn-sm" disabled={page<=1} onClick={() => setPage(p=>p-1)}>Prev</button>
            <span style={{padding:"0 8px",color:"var(--text2)"}}>Page {page} of {pages||1}</span>
            <button className="btn btn-ghost btn-sm" disabled={page>=pages} onClick={() => setPage(p=>p+1)}>Next</button>
          </div>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">New Promotion</div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Promo Code</label>
                  <input className="input" placeholder="WELCOME10" value={form.code} onChange={e => setForm(f=>({...f,code:e.target.value.toUpperCase()}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="select" value={form.type} onChange={e => setForm(f=>({...f,type:e.target.value}))}>
                    <option value="percentage">Percentage %</option>
                    <option value="fixed">Fixed Amount</option>
                    <option value="free_delivery">Free Delivery</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Value {form.type==="percentage"?"(%)":"(GHS)"}</label>
                  <input className="input" type="number" value={form.value} onChange={e => setForm(f=>({...f,value:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Min Order (GHS)</label>
                  <input className="input" type="number" value={form.min_order} onChange={e => setForm(f=>({...f,min_order:e.target.value}))}/>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Max Uses (blank = unlimited)</label>
                  <input className="input" type="number" placeholder="100" value={form.max_uses} onChange={e => setForm(f=>({...f,max_uses:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Expires At</label>
                  <input className="input" type="datetime-local" value={form.expires_at} onChange={e => setForm(f=>({...f,expires_at:e.target.value}))}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving||!form.code||!form.value}>
                {saving ? "Creating..." : "Create Promo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN SETTINGS PAGE ───────────────────────────────────────────────────────
export default function Settings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("commission");

  const visibleTabs = TABS.filter(t => t.roles.includes(user?.role));

  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.find(t => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [user?.role]);

  return (
    <div>
      <style>{`
        .settings-tabs {
          display: flex;
          gap: 4px;
          margin-bottom: 24px;
          border-bottom: 1px solid var(--border);
          overflow-x: auto;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
        }
        .settings-tabs::-webkit-scrollbar { display: none; }
        .settings-tab-btn {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 10px 16px;
          font-size: 13px;
          white-space: nowrap;
          background: none;
          border: none;
          cursor: pointer;
          margin-bottom: -1px;
          transition: all 0.15s;
          font-family: inherit;
          flex-shrink: 0;
        }
      `}</style>

      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-subtitle">Commission rates, delivery zones, and promotions</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:"var(--text3)"}}>
          <Shield size={13}/>
          <span style={{textTransform:"capitalize"}}>{user?.role?.replace(/_/g," ")} access</span>
        </div>
      </div>

      {/* Tab bar — scrollable on mobile */}
      <div className="settings-tabs">
        {visibleTabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              className="settings-tab-btn"
              onClick={() => setActiveTab(tab.id)}
              style={{
                fontWeight: isActive ? 700 : 500,
                color: isActive ? "var(--accent)" : "var(--text2)",
                borderBottom: isActive ? "2px solid var(--accent)" : "2px solid transparent",
              }}
            >
              <Icon size={14}/> {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "commission"  && <CommissionTab  user={user}/>}
      {activeTab === "zones"       && <ZonesTab       user={user}/>}
      {activeTab === "promotions"  && <PromotionsTab  user={user}/>}
    </div>
  );
}